import React from "react";
import { Link } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";

// What a page shows while its data loads, and after the call failed: grey skeleton blocks while it loads
// (never "Loading..." text, never a spinner for ever), and a failure that says so with a Try again button
// (never a blank page). Styles: .fn-pageskel, .fn-pagefail and .fn-cardfail in src/styles/foundation.css.
//
//   <PageLoadState failed={...} />   a whole page with nothing to show yet: navbar, skeleton or failure, footer
//   <PageSkeleton> / <PageFailed>    the same inside a page that draws its own navbar (Research, Factor School)
//   <CardFailed>                     one card's data failed; the page itself loaded

const FAILED_TEXT = "Its data couldn't be fetched. Check your connection and try again.";

// One grey block. w / h: CSS sizes (numbers are px); r: corner radius.
export const Skel = ({ w = "100%", h = 16, r = 6, style }) => (
  <span className="fn-skel" aria-hidden="true" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

// what: said to screen readers ("the research questions"). children: the page's own skeleton shapes;
// without them, a head (eyebrow, title, lead line) and four cards.
export const PageSkeleton = ({ what = "this page", children }) => (
  <div className="fn-pageskel" role="status" aria-live="polite">
    <span className="fn-sr">Getting {what} ready</span>
    {children || (
      <>
        <Skel w={120} h={14} />
        <Skel w="min(560px, 90%)" h={28} />
        <Skel w="min(680px, 100%)" h={16} />
        <div className="fn-pageskel-cards">
          {[0, 1, 2, 3].map((i) => (
            <Skel key={i} h={120} r={16} />
          ))}
        </div>
      </>
    )}
  </div>
);

// onRetry: what Try again does (the page loads its data again); without it, the page reloads.
// backTo / backLabel: a way out, as a quiet link.
export const PageFailed = ({ title = "This page didn't load", text, onRetry, backTo, backLabel }) => (
  <div className="fn-pagefail" role="alert">
    <h1 className="fn-pagefail-title">{title}</h1>
    <p className="fn-pagefail-text">{text || FAILED_TEXT}</p>
    <div className="fn-pagefail-row">
      <button type="button" className="fn-btn fn-btn-primary" onClick={onRetry || (() => window.location.reload())}>
        Try again
      </button>
      {backTo && (
        <Link to={backTo} className="fn-btn fn-btn-quiet">
          {backLabel || "Back"}
        </Link>
      )}
    </div>
  </div>
);

// A small failure inside a card.
export const CardFailed = ({ text, onRetry }) => (
  <div className="fn-cardfail" role="alert">
    <p>{text}</p>
    {onRetry && (
      <button type="button" className="fn-btn fn-btn-secondary fn-btn-sm" onClick={onRetry}>
        Try again
      </button>
    )}
  </div>
);

const PageLoadState = ({ failed = false, title, text, onRetry, backTo, backLabel, what }) => (
  <>
    <Navbar />
    <main className="fn-wrap fn-pagestate">
      {failed ? (
        <PageFailed title={title} text={text} onRetry={onRetry} backTo={backTo} backLabel={backLabel} />
      ) : (
        <PageSkeleton what={what} />
      )}
    </main>
    <Footer />
  </>
);

export default PageLoadState;
